import { Controller } from "@hotwired/stimulus"
import { hideAll } from "tippy.js"
import mapboxManager from "../../lib/mapbox_manager"
import LocationMapMarkers from "../../lib/location_map/markers"

export default class extends Controller {
  static targets = ["container", "data", "popup", "fallback", "marker"]

  connect() {
    if (!this.hasDataTarget) return
    this.cancelled = false
    this.started = false
    this.loaded = false
    this.locationMapId = `location-map-${crypto.randomUUID()}`
    this.settings = JSON.parse(this.dataTarget.textContent)
    this.locations = this.settings.locations.map((location) => ({
      ...location, coordinates: [Number(location.longitude), Number(location.latitude)],
    })).filter(({ coordinates: [longitude, latitude] }) =>
      Number.isFinite(longitude) && Number.isFinite(latitude) && Math.abs(longitude) <= 180 && Math.abs(latitude) <= 90)
    this.observer = new ResizeObserver(() => this.initializeMap())
    this.observer.observe(this.containerTarget)
    this.initializeMap()
  }

  disconnect() {
    this.cancelled = true
    this.observer?.disconnect()
    this.locationMapMarkers?.destroy()
    if (this.locationMapId) mapboxManager.destroyMap(this.locationMapId)
  }

  async initializeMap() {
    if (this.started || this.cancelled) return
    const { width, height } = this.containerTarget.getBoundingClientRect()
    if (width === 0 || height === 0) return
    if (!this.locations.length || !this.settings.accessToken) return
    this.started = true
    try {
      await mapboxManager.loadAPI(this.settings.accessToken)
      if (this.cancelled) return
      const [southwest, northeast] = this.bounds
      this.map = mapboxManager.createMapWithCache(this.containerTarget, {
        style: "mapbox://styles/mapbox/streets-v12",
        center: southwest.map((coordinate, index) => (coordinate + northeast[index]) / 2),
        zoom: this.settings.zoom, cacheKey: this.locationMapId,
      }, this.locationMapId)
      this.map.addControl(new window.mapboxgl.NavigationControl(), "top-right")
      // Mapbox reports dropped tiles, glyphs and sprites as "error" too, so only a
      // failure before the style loads means there is no map to show.
      this.map.on("error", () => { if (!this.loaded) this.showFallback() })
      this.map.on("load", () => {
        if (this.cancelled) return
        this.loaded = true
        this.fallbackTarget.classList.add("hidden")
        this.addLocations()
        if (this.settings.fitBounds && this.locations.length > 1) {
          this.map.fitBounds(this.bounds, { padding: 50, maxZoom: 10 })
        }
      })
    } catch {
      this.showFallback()
    }
  }

  get bounds() {
    const longitudes = this.locations.map(({ coordinates }) => coordinates[0])
    const latitudes = this.locations.map(({ coordinates }) => coordinates[1])
    return [[Math.min(...longitudes), Math.min(...latitudes)], [Math.max(...longitudes), Math.max(...latitudes)]]
  }

  addLocations() {
    this.locationMapMarkers = new LocationMapMarkers({
      map: this.map, locationMapId: this.locationMapId, markerTemplate: this.markerTarget,
      popupOptions: { closeButton: false, offset: [0, -15] },
      onShow: () => {
        // Card tooltips can open after the card's mouseenter handler.
        hideAll()
        requestAnimationFrame(() => { if (!this.cancelled) hideAll() })
      },
    })
    this.locations.forEach((location) => {
      const content = this.popupTarget.content.cloneNode(true)
      const link = content.querySelector("[data-location-map-popup-link]")
      link.href = location.url
      content.querySelector("[data-location-map-popup-title]").textContent = location.title
      content.querySelector("[data-location-map-popup-location]").textContent = location.location
      this.locationMapMarkers.add({ ...location, content })
    })
  }

  highlight(event) {
    const flight = this.settings?.focusZoom ? { zoom: Math.max(this.map?.getZoom() || 0, this.settings.focusZoom) } : {}
    this.locationMapMarkers?.show(event.currentTarget.dataset.locationMapRecordId, flight)
  }

  closePopup(event) {
    event.preventDefault()
    event.stopPropagation()
    this.locationMapMarkers?.close()
  }

  showFallback() {
    if (!this.cancelled) this.fallbackTarget.classList.remove("hidden")
  }
}
